// src/pages/LearnerDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase/config';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  getDoc,
} from 'firebase/firestore';
import '../styles/LearnerDashboard.css';

// Ordered sequence of placement levels
const PLACEMENT_LEVELS = ['level-1', 'level-2', 'level-3', 'level-4'];

const LEVEL_NAMES = {
  'level-1': 'Level 1 – Roots',
  'level-2': 'Level 2 – Branches',
  'level-3': 'Level 3 – Skies',
  'level-4': 'Level 4 – Rivers',
  'level-5': 'Level 5 – Mountains',
  'level-6': 'Level 6',
  'level-7': 'Level 7',
  'level-8': 'Level 8',
  'level-9': 'Level 9',
  'level-10': 'Level 10',
  'level-11': 'Level 11',
};

const PLACEMENT_PASS_THRESHOLD = 65;
const LEARN_APP_URL = 'https://learn.litvill.com';

const LearnerDashboard = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('placement');
  const [learnerLevelIds, setLearnerLevelIds] = useState([]);
  const [placementTests, setPlacementTests] = useState([]); // all placement tests
  const [lessonTests, setLessonTests] = useState([]);
  const [testHistory, setTestHistory] = useState({});
  const [loading, setLoading] = useState(true);

  // Whether this learner has any placement levels at all
  const hasPlacementLevels = learnerLevelIds.some(id => PLACEMENT_LEVELS.includes(id));
  // Whether this learner has lesson tests
  const hasLessonTests = lessonTests.length > 0;

  useEffect(() => {
    if (currentUser) loadDashboard();
  }, [currentUser]);

  // Switch default tab once data is loaded
  useEffect(() => {
    if (!loading) {
      if (hasPlacementLevels) setActiveTab('placement');
      else if (hasLessonTests) setActiveTab('knowledge');
    }
  }, [loading, hasPlacementLevels, hasLessonTests]);

  const loadDashboard = async () => {
    try {
      setLoading(true);

      // Step 1: enrolled catalogs
      const userCatalogsSnap = await getDocs(
        query(collection(db, 'userCatalogs'), where('userId', '==', currentUser.uid))
      );

      if (userCatalogsSnap.empty) {
        setLoading(false);
        return;
      }

      const catalogIds = userCatalogsSnap.docs.map(d => d.data().catalogId);
      const levelIds = [];

      await Promise.all(
        catalogIds.map(async (catalogId) => {
          const catalogSnap = await getDoc(doc(db, 'catalogs', catalogId));
          if (catalogSnap.exists()) {
            const levelId = catalogSnap.data().levelId;
            if (levelId && !levelIds.includes(levelId)) levelIds.push(levelId);
          }
        })
      );

      setLearnerLevelIds(levelIds);

      // Step 2: placement tests for relevant levels
      const relevantPlacementLevels = levelIds.filter(id => PLACEMENT_LEVELS.includes(id));
      if (relevantPlacementLevels.length > 0) {
        const placementSnap = await getDocs(
          query(collection(db, 'tests'), where('isActive', '==', true))
        );
        const allTests = placementSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const filtered = allTests.filter(
          t => t.mode === 'placement' && !t.lessonCode && PLACEMENT_LEVELS.includes(t.levelId)
        );
        setPlacementTests(filtered);
      }

      // Step 3: lesson tests
      if (levelIds.length > 0) {
        const lessonSnap = await getDocs(
          query(collection(db, 'lessonTests'), where('levelId', 'in', levelIds))
        );
        setLessonTests(lessonSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      }

      // Step 4: test history
      await loadTestHistory();

    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTestHistory = async () => {
    if (!currentUser) return;
    try {
      const attemptsSnap = await getDocs(
        query(
          collection(db, 'testAttempts'),
          where('userId', '==', currentUser.uid)
        )
      );
      // Sort client-side: most recent first
      const sorted = attemptsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''));
      const history = {};
      sorted.forEach(data => {
        if (!history[data.testId]) history[data.testId] = [];
        history[data.testId].push(data);
      });
      setTestHistory(history);
    } catch (error) {
      console.error('Error loading test history:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getLastAttempt = (testId) => (testHistory[testId] || [])[0];

  // ─── Placement status logic ────────────────────────────────────────────────

  /**
   * Returns the best (highest-score) passed attempt for a test, or null.
   */
  const getBestPassedAttempt = (testId) =>
    (testHistory[testId] || []).filter(a => a.passed).sort((a, b) => b.score - a.score)[0] || null;

  /**
   * Build a status object for each placement level in order.
   * Levels are unlocked sequentially: level-2 only after ALL THREE tests in level-1 are passed.
   *
   * Returns array of:
   *   {
   *     levelId, levelName, locked,
   *     levelStatus: 'not-started' | 'in-progress' | 'passed',
   *     tests: [{ test, status, lastAttempt, bestPassed, attemptCount }]
   *   }
   *
   * A level is 'passed' only when every one of its tests has a passing attempt.
   * A level is 'in-progress' when at least one test has been attempted but not all passed.
   */
  const buildPlacementStatus = () => {
    let previousLevelPassed = true; // Level 1 is always unlocked

    return PLACEMENT_LEVELS.map((levelId) => {
      const levelTests = placementTests
        .filter(t => t.levelId === levelId)
        .map(test => {
          const attempts = testHistory[test.id] || [];
          const lastAttempt = attempts[0] || null;
          const bestPassed = attempts
            .filter(a => a.passed)
            .sort((a, b) => b.score - a.score)[0] || null;

          let status = 'not-started';
          if (bestPassed) status = 'passed';
          else if (lastAttempt) status = 'attempted';

          return { test, status, lastAttempt, bestPassed, attemptCount: attempts.length };
        });

      const allPassed = levelTests.length > 0 && levelTests.every(t => t.status === 'passed');
      const anyAttempted = levelTests.some(t => t.attemptCount > 0);

      let levelStatus = 'not-started';
      if (allPassed) levelStatus = 'passed';
      else if (anyAttempted) levelStatus = 'in-progress';

      const locked = !previousLevelPassed;
      previousLevelPassed = allPassed;

      return {
        levelId,
        levelName: LEVEL_NAMES[levelId] || levelId,
        locked,
        levelStatus,
        tests: levelTests,
      };
    });
  };

  /**
   * Overall placement journey status:
   * - 'complete' if level-4 is passed
   * - 'enrolled' if any level is failed (learner has been auto-enrolled, check Learn app)
   * - 'in-progress' otherwise
   */
  const getJourneyStatus = (levels) => {
    if (levels.find(l => l.levelId === 'level-4' && l.levelStatus === 'passed')) return 'complete';
    if (levels.find(l => l.levelStatus === 'in-progress' || l.levelStatus === 'passed')) return 'in-progress';
    return 'not-started';
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  if (loading) return <div className="loading-screen">Loading your dashboard...</div>;

  if (learnerLevelIds.length === 0) {
    return (
      <div className="learner-dashboard">
        <DashboardHeader email={currentUser?.email} onLogout={handleLogout} />
        <main className="dashboard-main">
          <div className="empty-message">
            You are not enrolled in any courses yet. Please contact your administrator.
          </div>
        </main>
      </div>
    );
  }

  const placementStatus = buildPlacementStatus();
  const journeyStatus = getJourneyStatus(placementStatus);
  const lessonTestsByLevel = lessonTests.reduce((acc, t) => {
    if (!acc[t.levelId]) acc[t.levelId] = [];
    acc[t.levelId].push(t);
    return acc;
  }, {});

  return (
    <div className="learner-dashboard">
      <DashboardHeader email={currentUser?.email} onLogout={handleLogout} />

      {/* Tab Navigation */}
      <nav className="dashboard-tabs">
        {hasPlacementLevels && (
          <button
            className={`tab-btn ${activeTab === 'placement' ? 'active' : ''}`}
            onClick={() => setActiveTab('placement')}
          >
            📊 Placement Tests
          </button>
        )}
        {hasLessonTests && (
          <button
            className={`tab-btn ${activeTab === 'knowledge' ? 'active' : ''}`}
            onClick={() => setActiveTab('knowledge')}
          >
            📖 Knowledge Checks
          </button>
        )}
      </nav>

      <main className="dashboard-main">

        {/* ── Placement Status Screen ────────────────────────────────────── */}
        {activeTab === 'placement' && hasPlacementLevels && (
          <section className="tests-section">

            {/* Journey banner */}
            {journeyStatus === 'complete' && (
              <div className="journey-banner journey-banner--complete">
                <span className="journey-banner-icon">🏆</span>
                <div>
                  <strong>Placement complete!</strong>
                  <p>You've passed all four levels and been enrolled in the advanced courses on Litvill Lessons.</p>
                  <a href={LEARN_APP_URL} target="_blank" rel="noopener noreferrer" className="banner-link">
                    Go to Litvill Lessons →
                  </a>
                </div>
              </div>
            )}

            {journeyStatus !== 'complete' && (
              <p className="section-description">
                Pass all three tests at each level to unlock the next one. You can take them in any order.
                Pass threshold: 65%.
              </p>
            )}

            {/* Level cards — one accordion per level, tests listed inside */}
            <div className="placement-levels-list">
              {placementStatus.map((level, index) => (
                <PlacementLevelCard
                  key={level.levelId}
                  level={level}
                  stepNumber={index + 1}
                  navigate={navigate}
                />
              ))}
            </div>
          </section>
        )}

        {/* ── Knowledge Checks Tab ───────────────────────────────────────── */}
        {activeTab === 'knowledge' && (
          <section className="tests-section">
            <p className="section-description">
              Check your understanding of specific lessons before moving on.
            </p>

            {lessonTests.length === 0 ? (
              <div className="empty-message">No knowledge checks available yet.</div>
            ) : (
              <>
                {learnerLevelIds.length > 1 ? (
                  learnerLevelIds.map(levelId => {
                    const tests = lessonTestsByLevel[levelId];
                    if (!tests || tests.length === 0) return null;
                    return (
                      <div key={levelId} className="level-group">
                        <h3 className="level-heading">{LEVEL_NAMES[levelId] || levelId}</h3>
                        <div className="knowledge-checks-list">
                          {tests.map(test => renderKnowledgeCheckCard(test))}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="knowledge-checks-list">
                    {lessonTests.map(test => renderKnowledgeCheckCard(test))}
                  </div>
                )}
              </>
            )}
          </section>
        )}

      </main>
    </div>
  );

  function renderKnowledgeCheckCard(test) {
    const lastAttempt = getLastAttempt(test.id);
    const attempts = testHistory[test.id] || [];

    return (
      <div key={test.id} className="knowledge-check-card">
        <div className="knowledge-check-info">
          <h4 className="lesson-title">{test.lessonTitle}</h4>
        </div>
        <div className="knowledge-check-status">
          {attempts.length > 0 && lastAttempt && (
            <span className={`badge ${lastAttempt.passed ? 'passed' : 'failed'}`}>
              Last: {lastAttempt.score}%
            </span>
          )}
        </div>
        <div className="knowledge-check-actions">
          <button
            onClick={() => navigate(`/lesson-test/${test.id}`)}
            className="start-test-btn small"
          >
            {attempts.length > 0 ? 'Retake' : 'Start'}
          </button>
          {lastAttempt && (
            <button
              onClick={() => navigate(`/results/${lastAttempt.id}`)}
              className="view-results-btn small"
            >
              Results
            </button>
          )}
        </div>
      </div>
    );
  }
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function DashboardHeader({ email, onLogout }) {
  return (
    <header className="dashboard-header">
      <div className="header-content">
        <h1>My Tests</h1>
        <div className="user-info">
          <span className="user-email">{email}</span>
          <button onClick={onLogout} className="logout-btn">Logout</button>
        </div>
      </div>
    </header>
  );
}

/**
 * One card per placement level. Shows the level header (step badge, name, overall status)
 * and — when not locked — lists each of the three tests as individual rows.
 */
function PlacementLevelCard({ level, stepNumber, navigate }) {
  const { levelName, locked, levelStatus, tests } = level;

  const levelStatusConfig = {
    'not-started': { label: 'Not started',  className: 'status--not-started', icon: '○' },
    'in-progress': { label: 'In progress',  className: 'status--in-progress', icon: '◑' },
    'passed':      { label: 'Complete',     className: 'status--passed',       icon: '✓' },
  };

  const { label, className, icon } = locked
    ? { label: 'Locked', className: 'status--locked', icon: '🔒' }
    : (levelStatusConfig[levelStatus] || levelStatusConfig['not-started']);

  const passedCount = tests.filter(t => t.status === 'passed').length;

  return (
    <div className={[
      'placement-level-card',
      locked           ? 'placement-level-card--locked'  : '',
      levelStatus === 'passed' ? 'placement-level-card--passed' : '',
    ].join(' ')}>

      {/* ── Level header row ── */}
      <div className="placement-level-header">
        <div className={`placement-step-badge ${levelStatus === 'passed' ? 'placement-step-badge--passed' : ''}`}>
          {levelStatus === 'passed' ? '✓' : stepNumber}
        </div>

        <div className="placement-level-info">
          <h4 className="placement-level-name">{levelName}</h4>
          {!locked && (
            <p className="placement-level-meta">
              {passedCount} / {tests.length} tests passed
            </p>
          )}
        </div>

        <span className={`placement-status-badge ${className}`}>
          {icon} {label}
        </span>
      </div>

      {/* ── Individual test rows (only when unlocked) ── */}
      {!locked && tests.length > 0 && (
        <div className="placement-test-rows">
          {tests.map(({ test, status, lastAttempt, bestPassed, attemptCount }, i) => {
            const testStatusConfig = {
              'not-started': { label: 'Not started', className: 'status--not-started', icon: '○' },
              'attempted':   { label: 'Not passed',  className: 'status--failed',       icon: '✗' },
              'passed':      { label: 'Passed',       className: 'status--passed',       icon: '✓' },
            };
            const ts = testStatusConfig[status] || testStatusConfig['not-started'];

            return (
              <div key={test.id} className={`placement-test-row ${status === 'passed' ? 'placement-test-row--passed' : ''}`}>
                <span className="placement-test-label">
                  {test.title
                    ? test.title.includes(' - ')
                      ? test.title.split(' - ').pop()
                      : test.title
                    : `Test ${i + 1}`}
                </span>

                <span className={`placement-status-badge placement-status-badge--sm ${ts.className}`}>
                  {ts.icon} {ts.label}
                </span>

                {bestPassed && (
                  <span className="placement-best-score">Best: {bestPassed.score}%</span>
                )}
                {!bestPassed && lastAttempt && (
                  <span className="placement-last-score">Last: {lastAttempt.score}%</span>
                )}
                {attemptCount > 0 && (
                  <span className="placement-attempt-count">
                    {attemptCount} attempt{attemptCount !== 1 ? 's' : ''}
                  </span>
                )}

                <div className="placement-test-actions">
                  <button
                    onClick={() => navigate(`/test/${test.id}`)}
                    className={status === 'passed' ? 'view-results-btn small' : 'start-test-btn small'}
                  >
                    {status === 'not-started' ? 'Start' : 'Retake'}
                  </button>
                  {lastAttempt && (
                    <button
                      onClick={() => navigate(`/results/${lastAttempt.id}`)}
                      className="view-results-btn small"
                    >
                      Results
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
export default LearnerDashboard;